import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { Save, Refresh } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { format } from 'date-fns';
import api from '../../services/api';

const Transaction24K = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [currentDate] = useState(new Date());
  
  // First table data (4.1.1)
  const [firstTable, setFirstTable] = useState({
    ndb: 0,
    new1: 0,
    new2: 0,
    new3: 0,
    ch1: 0,
    ch2: 0,
    ex: 0,
    total: 0
  });

  // Second table data (4.1.2)
  const [secondTable, setSecondTable] = useState({
    column1: 0,
    column2: 0,
    column3: 0,
    column4: 0,
    column5: 0,
    column6: 0,
    total: 0
  });

  // Fourth table data (4.2.2) - Gold details
  const [goldDetails, setGoldDetails] = useState({
    jink: 0,
    chain: 0,
    ball: 0,
    work: 0,
    reject: 0
  });

  useEffect(() => {
    loadTransaction();
  }, []);

  const loadTransaction = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/transaction?date=${format(currentDate, 'yyyy-MM-dd')}&karat=24K`);
      if (response.data.length > 0) {
        const transaction = response.data[0];
        setFirstTable(transaction.firstTable || firstTable);
        setSecondTable(transaction.secondTable || secondTable);
        setGoldDetails(transaction.goldDetails || goldDetails);
      }
    } catch (error) {
      console.error('Error loading transaction:', error);
      setMessage('Error loading transaction data');
    }
    setLoading(false);
  };

  const calculateFirstTableTotal = () => {
    const total = firstTable.ndb + firstTable.new1 + firstTable.new2 + firstTable.new3 + 
                  firstTable.ch1 + firstTable.ch2 + firstTable.ex;
    setFirstTable(prev => ({ ...prev, total }));
  };

  const calculateSecondTableTotal = () => {
    const total = secondTable.column1 + secondTable.column2 + secondTable.column3 + 
                  secondTable.column4 + secondTable.column5 + secondTable.column6;
    setSecondTable(prev => ({ ...prev, total }));
  };

  const handleFirstTableChange = (field, value) => {
    setFirstTable(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const handleSecondTableChange = (field, value) => {
    setSecondTable(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const handleGoldDetailsChange = (field, value) => {
    setGoldDetails(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const saveTransaction = async () => {
    setSaving(true);
    setMessage('');
    
    try {
      const transactionData = {
        date: currentDate,
        karat: '24K',
        firstTable,
        secondTable,
        goldDetails
      };

      const response = await api.post('/api/transaction', transactionData);
      setMessage('Transaction saved successfully!');
    } catch (error) {
      console.error('Error saving transaction:', error);
      setMessage('Error saving transaction');
    }
    
    setSaving(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 8 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => window.history.back()} variant="outlined" color="primary" sx={{ mr: 2 }}>
          Back
        </Button>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            24K Gold Transaction
          </Typography>
          <Typography variant="h6" sx={{ textAlign: 'right' }}>
            Date: {format(currentDate, 'dd/MM/yyyy')}
          </Typography>
        </Box>
      </Box>

      {message && (
        <Alert severity={message.includes('Error') ? 'error' : 'success'} sx={{ mb: 3 }}>
          {message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* First Table (4.1.1) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              First Table (4.1.1)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Karat</TableCell>
                    <TableCell>NDB</TableCell>
                    <TableCell>NEW</TableCell>
                    <TableCell>NEW</TableCell>
                    <TableCell>NEW</TableCell>
                    <TableCell>CH</TableCell>
                    <TableCell>CH</TableCell>
                    <TableCell>EX</TableCell>
                    <TableCell>TOTAL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>9999</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={firstTable.ndb}
                        onChange={(e) => handleFirstTableChange('ndb', e.target.value)}
                        onBlur={calculateFirstTableTotal}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={firstTable.new1}
                        onChange={(e) => handleFirstTableChange('new1', e.target.value)}
                        onBlur={calculateFirstTableTotal}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={firstTable.new2}
                        onChange={(e) => handleFirstTableChange('new2', e.target.value)}
                        onBlur={calculateFirstTableTotal}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={firstTable.new3}
                        onChange={(e) => handleFirstTableChange('new3', e.target.value)}
                        onBlur={calculateFirstTableTotal}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={firstTable.ch1}
                        onChange={(e) => handleFirstTableChange('ch1', e.target.value)}
                        onBlur={calculateFirstTableTotal}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={firstTable.ch2}
                        onChange={(e) => handleFirstTableChange('ch2', e.target.value)}
                        onBlur={calculateFirstTableTotal}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={firstTable.ex}
                        onChange={(e) => handleFirstTableChange('ex', e.target.value)}
                        onBlur={calculateFirstTableTotal}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={firstTable.total}
                        InputProps={{ readOnly: true }}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Second Table (4.1.2) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Second Table (4.1.2)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Column 1</TableCell>
                    <TableCell>Column 2</TableCell>
                    <TableCell>Column 3</TableCell>
                    <TableCell>Column 4</TableCell>
                    <TableCell>Column 5</TableCell>
                    <TableCell>Column 6</TableCell>
                    <TableCell>TOTAL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <TextField
                        size="small"
                        value={secondTable.column1}
                        onChange={(e) => handleSecondTableChange('column1', e.target.value)}
                        onBlur={calculateSecondTableTotal}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={secondTable.column2}
                        onChange={(e) => handleSecondTableChange('column2', e.target.value)}
                        onBlur={calculateSecondTableTotal}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={secondTable.column3}
                        onChange={(e) => handleSecondTableChange('column3', e.target.value)}
                        onBlur={calculateSecondTableTotal}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={secondTable.column4}
                        onChange={(e) => handleSecondTableChange('column4', e.target.value)}
                        onBlur={calculateSecondTableTotal}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={secondTable.column5}
                        onChange={(e) => handleSecondTableChange('column5', e.target.value)}
                        onBlur={calculateSecondTableTotal}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={secondTable.column6}
                        onChange={(e) => handleSecondTableChange('column6', e.target.value)}
                        onBlur={calculateSecondTableTotal}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={secondTable.total}
                        InputProps={{ readOnly: true }}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Gold Details (4.2.2) */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              24K Gold Details (4.2.2)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Jink"
                  value={goldDetails.jink}
                  onChange={(e) => handleGoldDetailsChange('jink', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Chain"
                  value={goldDetails.chain}
                  onChange={(e) => handleGoldDetailsChange('chain', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Ball"
                  value={goldDetails.ball}
                  onChange={(e) => handleGoldDetailsChange('ball', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Work"
                  value={goldDetails.work}
                  onChange={(e) => handleGoldDetailsChange('work', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Reject"
                  value={goldDetails.reject}
                  onChange={(e) => handleGoldDetailsChange('reject', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Actions
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={saveTransaction}
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Save Transaction'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadTransaction}
              >
                Refresh
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Transaction24K; 